
import { Subscription } from "@/types/subscription";

export const validateSubscriptionData = (data: Omit<Subscription, "id" | "totalPaid">): Omit<Subscription, "id" | "totalPaid"> => {
  console.log("=== VALIDATING SUBSCRIPTION DATA ===");
  console.log("Input data:", data);
  
  // Validate required fields
  if (!data.customerName?.trim()) {
    throw new Error("Customer name is required");
  }
  
  if (!data.email?.trim()) {
    throw new Error("Email is required");
  }
  
  if (!data.address?.trim()) {
    throw new Error("Address is required");
  }
  
  if (!data.postcode?.trim()) {
    throw new Error("Postcode is required");
  }
  
  // Note: Bin collection day is automatically determined from address
  const binDay = data.binDay?.trim() || "To be determined automatically";
  
  // FIXED: Business validation - ensure business name is required for business subscriptions
  if (data.isBusiness === true && (!data.businessName || data.businessName.trim() === '')) {
    throw new Error("Business name is required for business subscriptions");
  }
  
  const validatedData = {
    ...data,
    customerName: data.customerName.trim(),
    email: data.email.trim(),
    address: data.address.trim(),
    postcode: data.postcode.trim(),
    binDay: binDay, // Use the calculated binDay value
    isBusiness: Boolean(data.isBusiness),
    businessName: data.isBusiness && data.businessName ? data.businessName.trim() : null,
    isAnnual: Boolean(data.isAnnual),
    multipleDays: Boolean(data.multipleDays),
    active: data.active !== undefined ? data.active : true,
    status: data.status || 'active'
  };
  
  console.log("Validated data:", validatedData);
  console.log("=== END VALIDATING SUBSCRIPTION DATA ===");
  
  return validatedData;
};

export const prepareSubscriptionForSupabaseData = (data: Omit<Subscription, "id" | "totalPaid">) => {
  const validatedData = validateSubscriptionData(data);
  
  console.log("=== PREPARE SUBSCRIPTION FOR SUPABASE - ENHANCED DEBUG ===");
  console.log("Incoming subscription data:", JSON.stringify(validatedData, null, 2));
  console.log("Business field analysis:", {
    isBusiness: validatedData.isBusiness,
    isBusiness_type: typeof validatedData.isBusiness,
    businessName: validatedData.businessName,
    businessName_type: typeof validatedData.businessName,
    businessName_length: validatedData.businessName ? validatedData.businessName.length : 'N/A',
    businessName_truthy: !!validatedData.businessName
  });

  // FIXED: Calculate correct weekly rate based on business vs residential
  const weeklyRate = validatedData.isBusiness === true ? 20 : 5;
  console.log("Calculated weekly rate:", {
    isBusiness: validatedData.isBusiness,
    weeklyRate: weeklyRate,
    reasoning: validatedData.isBusiness ? "Business rate (£20)" : "Residential rate (£5)"
  });

  // FIXED: Create the Supabase data object - ensure business fields are properly handled
  const supabaseData = {
    customer_name: validatedData.customerName,
    email: validatedData.email,
    address: validatedData.address,
    postcode: validatedData.postcode,
    bin_day: validatedData.binDay,
    start_date: validatedData.startDate,
    total_paid: 0,
    weekly_rate: weeklyRate, // FIXED: Set the correct weekly rate
    is_annual: Boolean(validatedData.isAnnual),
    active: validatedData.active !== undefined ? validatedData.active : true,
    status: validatedData.status || 'active',
    end_date: validatedData.endDate || null,
    // FIXED: Ensure business fields are properly set - be very explicit with validation
    is_business: validatedData.isBusiness === true,
    business_name: (validatedData.isBusiness === true && validatedData.businessName && validatedData.businessName.trim() !== '') 
      ? validatedData.businessName.trim() 
      : null,
    multiple_days: Boolean(validatedData.multipleDays)
  };
  
  console.log("Transformation details:", {
    original_isBusiness: validatedData.isBusiness,
    result_is_business: supabaseData.is_business,
    original_businessName: validatedData.businessName,
    result_business_name: supabaseData.business_name,
    calculated_weekly_rate: supabaseData.weekly_rate,
    business_name_will_be_saved: !!supabaseData.business_name
  });
  
  console.log("Final Supabase data being returned:", JSON.stringify(supabaseData, null, 2));
  console.log("=== END PREPARE SUBSCRIPTION FOR SUPABASE DEBUG ===");
  
  return supabaseData;
};

export const createLocalSubscription = (data: Omit<Subscription, "id" | "totalPaid">): Subscription => {
  const validatedData = validateSubscriptionData(data);
  
  return {
    id: crypto.randomUUID(),
    ...validatedData,
    totalPaid: 0,
  };
};
